import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitMerge, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { SimpleSelect, btnGhost, btnPrimary } from "@/components/app/common";

const LABELS = { righe_fattura: "righe di fattura", acquisti: "acquisti (costo medio)", vendite: "vendite mensili", uscite: "uscite merce", rettifiche: "rettifiche inventario", mappature: "abbinamenti salvati" };

export function UnisciDialog({ keep, prodotti, onClose, onDone, dupInit = "", onSwap }) {
  const [dup, setDup] = useState("");
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDup(dupInit); setPrev(null); }, [keep, dupInit]);
  useEffect(() => {
    setPrev(null);
    if (keep && dup) api.get(`/prodotti/${keep.id}/unisci/${dup}`).then((r) => setPrev(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [keep, dup]);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/prodotti/${keep.id}/unisci/${dup}`);
      toast.success(`Prodotti uniti in «${data.prodotto.nome}»`);
      onDone();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!keep} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-lg" data-testid="unisci-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitMerge className="h-5 w-5 text-[#8C6D62]" />Unisci prodotti</DialogTitle>
          <DialogDescription>Mantieni <b>{keep?.nome}</b> e unisci qui un duplicato creato per errore: acquisti, vendite, uscite, rettifiche e abbinamenti passano a questo prodotto, poi il duplicato viene eliminato.</DialogDescription>
        </DialogHeader>
        <SimpleSelect testId="unisci-duplicato-select" value={dup} onChange={setDup} placeholder="Scegli il duplicato da eliminare"
          options={prodotti.filter((p) => p.id !== keep?.id).map((p) => ({ value: p.id, label: `${p.nome}${p.sku ? ` · ${p.sku}` : ""}${p.marca ? ` (${p.marca})` : ""}` }))} />
        {prev && (
          <div className="rounded-xl bg-[#FAF7F5] p-4 text-sm space-y-2" data-testid="unisci-riepilogo">
            <div>Verrà eliminato <b>{prev.elimina.nome}</b>{prev.elimina.sku ? ` (SKU ${prev.elimina.sku})` : ""}. Saranno spostati:</div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">{Object.entries(prev.conteggi).map(([k, v]) => <li key={k} data-testid={`unisci-count-${k}`}><b className="tabular">{v}</b> {LABELS[k]}</li>)}</ul>
            <div className="text-xs text-[#756B64]">Giacenza iniziale sommata ({prev.mantieni.giacenza_iniziale} + {prev.elimina.giacenza_iniziale}). Costo medio ricalcolato su tutti gli acquisti (ora {prev.mantieni.costo_medio != null ? fmtEUR(prev.mantieni.costo_medio) : "—"} e {prev.elimina.costo_medio != null ? fmtEUR(prev.elimina.costo_medio) : "—"}).</div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          {onSwap && <Button variant="outline" className={`${btnGhost} mr-auto`} onClick={onSwap} disabled={busy} data-testid="unisci-inverti">Mantieni l'altro</Button>}
          <Button variant="outline" className={btnGhost} onClick={onClose} disabled={busy} data-testid="unisci-annulla">Annulla</Button>
          <Button className={btnPrimary} onClick={go} disabled={!prev || busy} data-testid="unisci-conferma">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unisci ed elimina duplicato"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
